/**
 * CategorySuggestion Component (T086-T092)
 *
 * Displays AI-powered category suggestion with ability to accept or override.
 * Shows manual category selector when AI confidence is low or fallback required.
 */

import React, { useState } from 'react'
import styles from './CategorySuggestion.module.css'
import type { CategorySuggestion as CategorySuggestionType } from '@/services/categorySuggestionService'

interface CategorySuggestionProps {
  suggestion: CategorySuggestionType
  onAccept: (category: string) => void
  onOverride: () => void
}

const CATEGORY_LABELS: Record<string, string> = {
  'marriage_relationship': 'Marriage & Relationship',
  'stress_anxiety': 'Stress & Anxiety',
  'not_defined': 'Not Defined / General'
}

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  'marriage_relationship': 'Issues with romantic partners, marriage, communication, intimacy',
  'stress_anxiety': 'Feeling overwhelmed, anxious, stressed, panic attacks, worry',
  'not_defined': 'General mental health concerns or multiple categories'
}

export const CategorySuggestion: React.FC<CategorySuggestionProps> = ({
  suggestion,
  onAccept,
  onOverride
}) => {
  const [showManualSelector, setShowManualSelector] = useState(suggestion.fallback_to_manual)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  const handleAcceptSuggestion = () => {
    onAccept(suggestion.suggested_category)
  }

  const handleShowManualSelector = () => {
    setShowManualSelector(true)
    onOverride()
  }

  const handleManualCategorySelect = (category: string) => {
    setSelectedCategory(category)
  }

  const handleConfirmManualSelection = () => {
    if (selectedCategory) {
      onAccept(selectedCategory)
    }
  }

  const confidencePercentage = Math.round(suggestion.confidence * 100)
  const isHighConfidence = suggestion.confidence >= 0.7

  // T091: Fallback to manual selection
  if (showManualSelector) {
    return (
      <div
        className={styles.categorySuggestionContainer}
        role="region"
        aria-label="Category selection"
      >
        <div className={styles.header}>
          <h3 className={styles.title}>Select Assessment Category</h3>
          {suggestion.error && (
            <div className={styles.errorMessage} role="alert">
              � {suggestion.error}
            </div>
          )}
          {!isHighConfidence && !suggestion.error && (
            <p className={styles.subtitle}>
              Please select a category that best describes your situation
            </p>
          )}
        </div>

        <div className={styles.manualSelectorContainer}>
          {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`${styles.categoryOption} ${selectedCategory === key ? styles.selected : ''}`}
              onClick={() => handleManualCategorySelect(key)}
              aria-label={`Select ${label} category`}
            >
              <div className={styles.categoryHeader}>
                <span className={styles.categoryIcon}>
                  {key === 'marriage_relationship' && '=�'}
                  {key === 'stress_anxiety' && '=0'}
                  {key === 'not_defined' && '=�'}
                </span>
                <span className={styles.categoryLabel}>{label}</span>
              </div>
              <p className={styles.categoryDescription}>
                {CATEGORY_DESCRIPTIONS[key]}
              </p>
            </button>
          ))}
        </div>

        {selectedCategory && (
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.confirmButton}
              onClick={handleConfirmManualSelection}
              aria-label="Confirm category selection and start assessment"
            >
              Start Assessment
            </button>
          </div>
        )}
      </div>
    )
  }

  // T086-T090: Display AI suggestion
  return (
    <div
      className={styles.categorySuggestionContainer}
      role="region"
      aria-label="AI category suggestion"
    >
      <div className={styles.header}>
        <h3 className={styles.title}>AI Recommendation</h3>
        <div className={`${styles.confidenceBadge} ${isHighConfidence ? styles.high : styles.medium}`}>
          {confidencePercentage}% confident
        </div>
      </div>

      <div className={styles.suggestionContent}>
        <div className={styles.categoryDisplay}>
          <span className={styles.categoryIcon}>
            {suggestion.suggested_category === 'marriage_relationship' && '=�'}
            {suggestion.suggested_category === 'stress_anxiety' && '=0'}
            {suggestion.suggested_category === 'not_defined' && '=�'}
          </span>
          <h4 className={styles.categoryName}>
            {CATEGORY_LABELS[suggestion.suggested_category]}
          </h4>
        </div>

        <div className={styles.reasoning}>
          <p className={styles.reasoningLabel}>Why this category?</p>
          <p className={styles.reasoningText}>{suggestion.reasoning}</p>
        </div>

        {suggestion.keywords_detected.length > 0 && (
          <div className={styles.keywords}>
            <p className={styles.keywordsLabel}>Keywords detected:</p>
            <div className={styles.keywordsList}>
              {suggestion.keywords_detected.map((keyword, index) => (
                <span key={index} className={styles.keyword}>
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.acceptButton}
          onClick={handleAcceptSuggestion}
          aria-label={`Start ${CATEGORY_LABELS[suggestion.suggested_category]} assessment`}
        >
          <span className={styles.buttonIcon}></span>
          <span className={styles.buttonText}>Start This Assessment</span>
        </button>

        <button
          type="button"
          className={styles.overrideButton}
          onClick={handleShowManualSelector}
          aria-label="Choose a different category"
        >
          <span className={styles.buttonIcon}>�</span>
          <span className={styles.buttonText}>Choose Different Category</span>
        </button>
      </div>
    </div>
  )
}
